// src/hooks/useWorkshopCreation.js
import { useCallback } from "react";
import { useWorkshops } from "../contexts/WorkshopContext";
import { useStudio } from "../contexts/StudioContext";
import { upsertWorkshopRemote } from "../lib/workshopsUpsert";
import { saveWorkshopSupabase } from "../lib/mudoNetwork/workshopsApi";
import { supabase } from "../supabaseClient";

/**
 * Converts date string (YYYY-MM-DD) and time string (HH:MM)
 * to ISO string in local timezone
 */
export const toLocalStartISO = (dateStr, timeStr) => {
  const y = Number(dateStr?.slice(0, 4));
  const m = Number(dateStr?.slice(5, 7)) - 1;
  const d = Number(dateStr?.slice(8, 10));
  const hh = Number(timeStr?.slice(0, 2));
  const mm = Number(timeStr?.slice(3, 5));
  const dt = new Date(y, m, d, hh, mm, 0, 0);
  return dt.toISOString();
};

/**
 * Formats materials array for workshop creation
 */
export const formatMaterialsPerPerson = (materialsArray = []) => {
  return (materialsArray || [])
    .map((m) => ({
      name: (m.name || "").trim(),
      qty: Number(m.qty) || 0,
      unit: m.unit || "g",
    }))
    .filter((m) => m.name && m.qty > 0);
};

/**
 * Hook to handle workshop creation with shared logic
 * Both Dashboard "Quick Create" and detailed "CreateWorkshopPanel" use this
 */
export const useWorkshopCreation = () => {
  const { createWorkshop, upsertWorkshop } = useWorkshops();
  const { studio: activeStudio } = useStudio() || {};

  /**
   * Create a workshop with full data
   * @param {Object} formData - Form data including:
   *   - title: string
   *   - description: string
   *   - date: string (YYYY-MM-DD)
   *   - time: string (HH:MM)
   *   - durationMins: number (optional, default 120)
   *   - capacity: number
   *   - price: number
   *   - discountPercent: number
   *   - type: string
   *   - level: string
   *   - materialsPerPerson: array of {name, qty, unit}
   *   - teaserDataUrl: string or null (image data URL)
   *   - isPublic: boolean (optional, default true)
   * @param {Object} options - Optional config:
   *   - onSuccess: callback after successful creation
   *   - onError: callback on error
   *   - skipSync: boolean - skip Supabase sync if true
   * @returns {Promise<Object>} - The created workshop object
   */
  const handleCreateWorkshop = useCallback(
    async (formData, options = {}) => {
      const { onSuccess, onError, skipSync = false } = options;

      try {
        // Validate required fields
        if (!formData.title?.trim()) {
          throw new Error("Workshop title is required");
        }
        if (!formData.date || !formData.time) {
          throw new Error("Date and time are required");
        }

        // Convert date/time to ISO
        const startISO = toLocalStartISO(formData.date, formData.time);

        // Format materials
        const materialsPerPerson = formatMaterialsPerPerson(
          formData.materialsPerPerson
        );

        // Build workshop data object
        const workshopData = {
          title: formData.title.trim(),
          description: (formData.description || "").trim(),
          startISO,
          durationMins: Math.max(1, Number(formData.durationMins) || 120),
          capacity: Math.max(1, Number(formData.capacity) || 1),
          price: Math.max(0, Number(formData.price) || 0),
          discountPercent: Math.max(0, Math.min(100, Number(formData.discountPercent) || 0)),
          type: formData.type || "Hand-building",
          level: formData.level || "All Levels",
          materialsPerPerson,
          teaserDataUrl: formData.teaserDataUrl || null,
          isPublic: formData.isPublic ?? true,
        };

        // ✅ CRITICAL FIX: Save to Supabase FIRST (not to local Drive)
        // This ensures the workshop is immediately visible in public.workshops table
        let supabaseWorkshop;
        try {
          console.log('[useWorkshopCreation] Saving to Supabase FIRST...');
          supabaseWorkshop = await saveWorkshopSupabase({
            ...workshopData,
            studio_id: activeStudio?.id,
          });
          console.log('[useWorkshopCreation] Supabase save SUCCESS:', {
            id: supabaseWorkshop.id,
            title: supabaseWorkshop.title,
            studio_id: supabaseWorkshop.studio_id,
          });
        } catch (supabaseErr) {
          // ❌ If Supabase fails, do NOT pretend it saved
          console.error('[useWorkshopCreation] Supabase save FAILED (critical!):', supabaseErr);
          throw new Error(`Failed to save to Supabase: ${supabaseErr?.message || supabaseErr}. Workshop NOT created.`);
        }

        // ✅ Now that Supabase has the authoritative copy, update local Drive cache
        const localWorkshop = await createWorkshop(supabaseWorkshop);
        console.log('[useWorkshopCreation] Updated Drive cache:', { id: localWorkshop.id });

        if (typeof onSuccess === "function") {
          onSuccess(localWorkshop);
        }

        return localWorkshop;
      } catch (err) {
        if (typeof onError === "function") {
          onError(err);
        }
        throw err;
      }
    },
    [createWorkshop, upsertWorkshop, activeStudio?.id]
  );

  return {
    handleCreateWorkshop,
    toLocalStartISO,
    formatMaterialsPerPerson,
  };
};

export default useWorkshopCreation;
